import type { ComponentType } from 'react';

import EventBusInstance from '@core/eventbus';
import { injectReact } from '@shared/render-utils';
import { CookieCategory } from '@shared-types/optanon.types';
import { get } from '@utilities/collection-utils';
import { getCookie } from '@utilities/cookies';
import { WeakCache } from '@utilities/memory-cache';
import { appendScript, waitForScript } from '@utilities/scripts';

export class CookieConsentService {
  async init() {
    const optanonIntance = await waitForScript('Optanon');
    optanonIntance.OnConsentChanged(() => EventBusInstance.dispatch('cookie-consent-changed'));
  }

  get active() {
    return !!window.Optanon || window.OptanonActive;
  }

  get activeGroups() {
    return window.OptanonActiveGroups ? window.OptanonActiveGroups.split(',') : null;
  }

  getCookiesAccepted(groupId: CookieCategory = CookieCategory.ESSENTIAL) {
    if (this.activeGroups) return this.activeGroups.includes(`${groupId}`);

    return !!getCookie('cookieconsent_dismissed');
  }

  onConsentChanged(callback: () => void) {
    EventBusInstance.on('cookie-consent-changed', callback);
  }

  /**
   * Return a promise resolved when the cookies are accepted
   * @param groupId the cookie group id
   * @param placeholderOptions placeholder setup
   * @param placeholderOptions.containers placeholder container elements
   * @param placeholderOptions.Placeholder placeholder component
   * @param placeholderOptions.text placeholder text
   * @returns a promise resolved when the cookies are acceted
   */
  waitForCookiesAccepted(
    groupId: CookieCategory,
    placeholderOptions?: { containers?: HTMLElement[]; Placeholder?: ComponentType; text?: string },
  ): Promise<boolean> {
    placeholderOptions && this.addPlaceholder(placeholderOptions, groupId);

    return new Promise((resolve) => {
      if (this.getCookiesAccepted(groupId)) return resolve(true);

      this.onConsentChanged(() => {
        if (this.getCookiesAccepted(groupId)) {
          return resolve(true);
        }
      });
    });
  }

  cache = new WeakCache<string, Promise<unknown>>();

  /**
   * Use this to load async a cookie involved script passing a callback for the script load
   * @param url the script URL
   * @param groupId the cookie group id
   * @param name the window property name set by the script (optional)
   * @param placeholderOptions placeholder setup
   * @param placeholderOptions.containers placeholder container elements
   * @param placeholderOptions.Placeholder placeholder component
   * @param placeholderOptions.text placeholder text
   * @returns promise resolved when the cookie is loaded
   */
  appendCookieScript<T = any>(
    url: string,
    groupId: CookieCategory,
    name?: string,
    placeholderOptions?: { containers?: HTMLElement[]; Placeholder?: ComponentType; text?: string },
  ): Promise<T> {
    placeholderOptions && this.addPlaceholder(placeholderOptions, groupId);

    return this.cache.getOrSet(
      url,
      () =>
        new Promise<T>(async (resolve, reject) => {
          if (name) {
            const windowEntity = get(window, name) as T;
            if (windowEntity) {
              return resolve(windowEntity);
            }
          }

          if (!url) reject(new Error('Script url is missing!!!'));

          if (this.active) await this.waitForCookiesAccepted(groupId);

          const obj = await appendScript<T>(url, name, name);
          return resolve(obj);
        }),
    ) as Promise<T>;
  }

  placeholderClassName = 'cookie-consent__placeholder__container';

  /**
   *
   * @param options placeholder setup
   * @param options.containers placeholder container elements
   * @param options.Placeholder placeholder component
   * @param options.text placeholder text
   * @param groupId the cookie group id
   */
  addPlaceholder(
    options: { containers?: HTMLElement[]; Placeholder?: ComponentType; text?: string },
    groupId: CookieCategory,
  ) {
    if (!this.active || this.getCookiesAccepted(groupId)) return;

    if (!options.containers || !options.Placeholder) return;

    Array.from(options.containers).forEach((container) => {
      if (!container) return;

      let placeholderWrapper = container.querySelector<HTMLElement>(`.${this.placeholderClassName}`);
      if (!placeholderWrapper) {
        placeholderWrapper = document.createElement('div');
        placeholderWrapper.className = this.placeholderClassName;
        container.appendChild(placeholderWrapper);
      }
      injectReact(options.Placeholder, { text: options.text, groupId }, placeholderWrapper);
    });
  }

  static readonly instance = new CookieConsentService();
}

const CookieConsentServiceInstance = CookieConsentService.instance;
CookieConsentServiceInstance.init();

export default CookieConsentServiceInstance;
